import { zodResolver } from '@hookform/resolvers/zod';
import { type EmailConfig, type Property, User } from '@kalos/kalos-rpc';
// USER_CONTACTS DISABLED (temporary): UserContact used only by the disabled additional-contact write path.
// import { type EmailConfig, type Property, User, UserContact } from '@kalos/kalos-rpc';
import {
  Button,
  Checkbox,
  Form as FormContext,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  LoadingIcon,
  PhoneInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  toast,
} from '@kalos/ui';
import { ChevronDownIcon, ChevronRightIcon } from '@radix-ui/react-icons';
// USER_CONTACTS DISABLED (temporary): Plus/X icons used only by the disabled additional phone/email rows.
// import { Plus, X } from 'lucide-react';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { type Country } from 'react-phone-number-input';
import { z } from 'zod';

import { AddressSearchInput } from '../../../../components/AddressSearchInput';
import { USA_STATES_OPTIONS } from '../../../../constants';
import { useContactPreferenceListQuery } from '../../../../hooks/react-query/useContactPreference';
import { getKalosLogoUrl } from '../../../../hooks/react-query/useKalosLogoQuery';
import { useReferralSourceListQuery } from '../../../../hooks/react-query/useReferralSource';
// USER_CONTACTS DISABLED (temporary):
// import { useUserContactCreateMutation } from '../../../../hooks/react-query/useUserContactQuery';
import {
  useSearchUserPropertiesQuery,
  useSearchUserQuery,
  useUserCreateMutation,
} from '../../../../hooks/react-query/useUserQuery';
import { EmailClientService } from '../../../../tools/helpers';
import { requiredPhoneNumberSchema } from '../../../../tools/schemas';
// USER_CONTACTS DISABLED (temporary):
// import { USER_CONTACT_KINDS } from '../../UserContacts';
import { CustomerJobsAccordion } from './CustomerJobsAccordion';

// -------------------- Types --------------------
export interface PropertyAddressData {
  streetAddress: string;
  city: string;
  state: string;
  zipCode: string;
  storeNumber?: string;
  notes?: string;
  propertyOwnershipTypeId: number;
}

export interface CustomerStepProps {
  onSelectCustomer: (customer: User) => void;
  onCreateCustomer: (customer: User, propertyData: PropertyAddressData) => void;
  onSelectProperty: (property: Property, customer?: User) => void;
  initialSearch?: string;
  onSearchChange?: (value: string) => void;
}

const allowedCountries: Country[] = ['US'];

// -------------------- Utility functions --------------------
const removePhonePrefix = (phone: string): string => {
  return phone.replace(/^\+1/, '');
};

// -------------------- New-customer form --------------------
const newCustomerSchema = z
  .object({
    firstName: z.string().min(1, { message: 'First name is required' }),
    lastName: z.string().min(1, { message: 'Last name is required' }),
    phone: requiredPhoneNumberSchema,
    email: z
      .string()
      .optional()
      .refine((val) => !val || /.+@.+\..+/.test(val), {
        message: 'Please enter a valid email',
      }),
    company: z.string().optional(),
    additionalPhones: z.array(
      z.object({
        value: requiredPhoneNumberSchema,
        typeId: z.string(),
      }),
    ),
    additionalEmails: z.array(
      z.object({
        value: z.string().refine((val) => /.+@.+\..+/.test(val), {
          message: 'Please enter a valid email',
        }),
        typeId: z.string(),
      }),
    ),
    referralSourceId: z.string().optional(),
    contactPreferenceId: z.string().optional(),
    referralSourceOther: z.string().optional(),
    streetAddress: z.string().min(1, { message: 'Street address is required' }),
    city: z.string().min(1, { message: 'City is required' }),
    state: z.string().min(1, { message: 'State is required' }),
    zipCode: z
      .string()
      .min(1, { message: 'ZIP code is required' })
      .regex(/^\d{5}(-\d{4})?$/, {
        message: 'ZIP code must be 5 digits or 5+4 format (e.g., 12345 or 12345-6789)',
      }),
    propertyOwnershipTypeId: z.string().min(1, { message: 'Property ownership is required' }),
    storeNumber: z.string().optional(),
    propertyNotes: z.string().optional(),
    billingSameAsProperty: z.boolean(),
    billingStreetAddress: z.string().optional(),
    billingCity: z.string().optional(),
    billingState: z.string().optional(),
    billingZipCode: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (!data.billingSameAsProperty) {
      if (!data.billingStreetAddress?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Billing street address is required',
          path: ['billingStreetAddress'],
        });
      }
      if (!data.billingCity?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'City is required',
          path: ['billingCity'],
        });
      }
      if (!data.billingState?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'State is required',
          path: ['billingState'],
        });
      }
      if (!data.billingZipCode || !/^\d{5}(-\d{4})?$/.test(data.billingZipCode)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'ZIP code must be 5 digits or 5+4 format (e.g., 12345 or 12345-6789)',
          path: ['billingZipCode'],
        });
      }
    }
  });

export type NewCustomerFormData = z.infer<typeof newCustomerSchema>;

// -------------------- Component --------------------
export const CustomerStep = ({
  onSelectCustomer,
  onCreateCustomer,
  onSelectProperty,
  initialSearch = '',
  onSearchChange,
}: CustomerStepProps) => {
  // -------------------- Tabs --------------------
  const [tab, setTab] = useState<'search' | 'new'>(initialSearch ? 'search' : 'search');

  // -------------------- Search --------------------
  const [searchValue, setSearchValue] = useState(initialSearch);

  // -------------------- Accordion State --------------------
  const [expandedSections, setExpandedSections] = useState<{
    customers: boolean;
    properties: boolean;
  }>({
    customers: true,
    properties: true,
  });

  // -------------------- Jobs Accordion State --------------------
  const [expandedCustomerJobs, setExpandedCustomerJobs] = useState<number | null>(null);
  const [expandedPropertyJobs, setExpandedPropertyJobs] = useState<number | null>(null);

  // propagate search changes
  useEffect(() => {
    onSearchChange?.(searchValue);
  }, [searchValue, onSearchChange]);

  const searchFilters = useMemo(() => {
    // The search endpoint on backend will look into multiple fields.
    // We now use the searchTarget field for unified search.
    if (searchValue.length < 2) return {};

    return {
      searchTarget: searchValue,
    };
  }, [searchValue]);

  const { data: searchResults, isFetching: isSearching } = useSearchUserQuery({
    filters: searchFilters,
    enabled: searchValue.length >= 2,
    options: {
      refetchOnWindowFocus: false,
    },
  });

  const { data: propertyResults, isFetching: isPropertySearching } = useSearchUserPropertiesQuery({
    searchTerm: searchValue,
    enabled: searchValue.length >= 2,
  });

  // Extract results from the search response
  const customers = searchResults?.results || [];
  const userProperties = propertyResults?.results || [];

  // -------------------- New customer form --------------------
  const newCustomerForm = useForm<NewCustomerFormData>({
    resolver: zodResolver(newCustomerSchema),
    mode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      company: '',
      additionalPhones: [],
      additionalEmails: [],
      referralSourceId: '',
      contactPreferenceId: '',
      referralSourceOther: '',
      streetAddress: '',
      city: '',
      state: '',
      zipCode: '',
      propertyOwnershipTypeId: '1',
      storeNumber: '',
      propertyNotes: '',
      billingSameAsProperty: true,
      billingStreetAddress: '',
      billingCity: '',
      billingState: '',
      billingZipCode: '',
    },
  });

  // USER_CONTACTS DISABLED (temporary):
  // const additionalPhonesArray = useFieldArray({
  //   control: newCustomerForm.control,
  //   name: 'additionalPhones',
  // });
  // const additionalEmailsArray = useFieldArray({
  //   control: newCustomerForm.control,
  //   name: 'additionalEmails',
  // });

  const createCustomerMutation = useUserCreateMutation();
  // USER_CONTACTS DISABLED (temporary):
  // const createUserContactMutation = useUserContactCreateMutation();

  const { data: referralSources = [] } = useReferralSourceListQuery();
  const { data: contactPreferences = [] } = useContactPreferenceListQuery();
  const selectedReferralSourceId = newCustomerForm.watch('referralSourceId');
  const isReferralOther =
    referralSources.find((s) => String(s.id) === selectedReferralSourceId)?.label === 'Other';

  const sendEmail = async (newUser: User) => {
    const logoUrl = await getKalosLogoUrl('onDark');
    const cleanedPassword = removePhonePrefix(newUser.pwd);
    const body = `<body>

    <table cellpadding="0" cellspacing="0" class="main-table" style="max-width:800; margin:auto; border:1px solid #ddd; background-color:#f1f1f1;" align="center">
     <tr>
    <td height="12"></td>
  </tr>
  <tr>
    <td>
    </td>
  </tr>

    <tr>
      <td height="10" colspan="3"></td>
    </tr>
    <tr>
      <td width="2%"></td>
      <td width="96%">
        <table style="width:100%; margin:auto; background-color:#fff;" cellpadding="0" cellspacing="0">
          <tr>
            <td width="2%"></td>
            <td width="96%">
              <table style="width:100%;" cellpadding="0" cellspacing="0">
                <tr>
                  <td height="12"></td>
                </tr>
                <a><img src='${logoUrl}' width="175"	alt="email-logo" style="display:block; background-color: #711313"/></a>

                <tr>
                  <td height="23"></td>
                </tr>
                <tr>
                  <td height="22"></td>
                </tr>
                <tr>
                  <td>
                    <h3 style="font-size:20px; color:#4d4d4d; font-family:Arial, Helvetica, sans-serif; margin:0;">
                         <b>Welcome to Kalos Services!</b>
                    </h3>
                  </td>
                </tr>
                <tr>
                  <td height="14"></td>
                </tr>
                <tr>
                  <td>
                    Customer Account Login:
                    <p style="margin:1; font-size:14px; color:#262626; line-height:20px; font-family:Arial, Helvetica, 'sans-serif';">

            Your username is:<b>${newUser.email} </b> <br>
            Your password is: <b>${cleanedPassword} </b>(case-sensitive - must be changed upon initial login)
            <br><br>
            <a href="https://customer.kalosflorida.com/auth/login" style="color:#711313; text-decoration:none; font-weight:bold;">Click here to login to your account</a>
                    </p>
                  </td>
                </tr>
                <tr>
                  <td height="10"></td>
                </tr>
                <tr>


                  <td height="10"></td>
                </tr>
                <tr>
                  <td>
                    <table style="width:100%;" cellpadding="1" cellspacing="1">
                      <tr>

        Thanks again for choosing Kalos Services Inc.
                        <br>
                        <br>
                                         <b>The Kalos Services Staff</b>
                        <br>
                                          (352) 243-7088
<p style="margin:1; font-size:14px; color:#262626; line-height:20px; font-family:Arial, Helvetica, 'sans-serif';">
      Call or text 352-243-7099 for simply great service!
<br>
  <br>
We offer 24 hour HVAC emergency service & air conditioning preventative maintenance.
    <br>
<a href="https://www.kalosflorida.com/service/air-conditioning-maintenance/">https://www.kalosflorida.com/service/air-conditioning-maintenance/</a>

                    </p>



                    </table>

                  </td>
                </tr>
                <tr>
                  <td height="18"></td>
                </tr>
                <tr>

                </tr>
                  <tr>
                  <td height="18"></td>
                </tr>
              </table>
            </td>
            <td width="2%"></td>
          </tr>
        </table>
      </td>
      <td width="2%"></td>
    </tr>
    <tr>
      <td height="13" colspan="3"></td>
    </tr>
    <tr>
      <td width="2%"></td>
      <td width="96%">
           <table style="width:100%; border-top:1px solid #d8d8d8;" cellpadding="0" cellspacing="0">
          <tr>
            <td height="10" colspan="2"></td>
          </tr>
          <tr>
            <td width="80%" align="right" valign="middle">
              <p style="font-size:10px; color:#595959; font-family:Arial, Helvetica, sans-serif; font-weight:300; line-height:13px; text-align:left" >
               This message has been sent automatically from http://app.KalosFlorida.com.
                                          If you have any questions or concerns regarding this document,
                                          please either reply to this email or call (352) 243-7088<br />

              </p>
            </td>
            <td width="20%" align="right">
            </td>
          </tr>
        </table>
      </td>
      <td width="2%"></td>
    </tr>
    <tr>
      <td height="30" colspan="3"></td>
    </tr>
  </table>
  </body>`;

    const email: EmailConfig = {
      recipient: newUser.email,
      subject: 'Subj. KalosFlorida.com New Account Confirmation ',
      from: 'office@kalosflorida.com',
      body,
    };

    await EmailClientService.sendMail(email);
  };

  const handleCreateCustomer: SubmitHandler<NewCustomerFormData> = (data) => {
    const cleanedPhone = removePhonePrefix(data.phone);

    // Billing address: use property address when checkbox is checked, separate fields otherwise
    const billingAddress = data.billingSameAsProperty
      ? {
          address: data.streetAddress.trim(),
          city: data.city.trim(),
          state: data.state,
          zip: data.zipCode,
        }
      : {
          address: data.billingStreetAddress!.trim(),
          city: data.billingCity!.trim(),
          state: data.billingState!,
          zip: data.billingZipCode!,
        };

    const userReq = User.create({
      firstname: data.firstName.trim(),
      lastname: data.lastName.trim(),
      phone: data.phone,
      email: data.email ?? '',
      businessname: data.company ?? '',
      referralSourceId: Number(data.referralSourceId) || 0,
      contactPreferenceId: Number(data.contactPreferenceId) || 0,
      referralSourceOther: isReferralOther ? data.referralSourceOther ?? '' : '',
      ...billingAddress,
      login: data.email ?? data.phone,
      pwd: cleanedPhone, // temporary password without + prefix
      isActive: 1,
      isEmployee: 0,
    });

    createCustomerMutation.mutate(userReq, {
      onSuccess: async (u) => {
        toast({
          title: 'Customer created',
          description: `${u.firstname} ${u.lastname} was created successfully.`,
          variant: 'success',
        });

        // Send welcome email if email is provided
        if (u.email) {
          try {
            await sendEmail(u);
          } catch (error) {
            console.error('Failed to send welcome email:', error);
            toast({
              title: 'Email notification failed',
              description: 'Customer was created but welcome email could not be sent.',
              variant: 'destructive',
            });
          }
        }

        // USER_CONTACTS DISABLED (temporary): skip writing additional phone/email contacts on create.
        // const extraContacts = [
        //   ...data.additionalPhones.map((p) => ({ value: p.value.trim(), typeId: p.typeId })),
        //   ...data.additionalEmails.map((e) => ({ value: e.value.trim(), typeId: e.typeId })),
        // ].filter((c) => c.value);
        // for (const contact of extraContacts) {
        //   try {
        //     await createUserContactMutation.mutateAsync(
        //       UserContact.create({
        //         userId: u.id,
        //         userContactTypeId: Number(contact.typeId),
        //         value: contact.value,
        //         isPrimary: 0,
        //         isActive: 1,
        //       }),
        //     );
        //   } catch (error) {
        //     console.error('Failed to save additional contact:', error);
        //     toast({
        //       title: 'Failed to save additional contact',
        //       description: contact.value,
        //       variant: 'destructive',
        //     });
        //   }
        // }

        // Property address is always captured on this screen
        const propertyData: PropertyAddressData = {
          streetAddress: data.streetAddress.trim(),
          city: data.city.trim(),
          state: data.state,
          zipCode: data.zipCode,
          storeNumber: data.storeNumber?.trim() ?? '',
          notes: data.propertyNotes ?? '',
          propertyOwnershipTypeId: Number(data.propertyOwnershipTypeId),
        };

        onCreateCustomer(u, propertyData);
      },
      onError: () => {
        toast({
          title: 'Failed to create customer',
          description: 'Please try again.',
          variant: 'destructive',
        });
      },
    });
  };

  const toggleSection = (section: 'customers' | 'properties') => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // -------------------- Render helpers --------------------
  const renderSearchTab = () => (
    <div className="space-y-4">
      {/* Search input */}
      <Input
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
        placeholder="Search name, phone, email or address"
        className="h-12 text-lg"
      />

      {/* Grows with its content, capped so the dialog still fits. relative contains the
          sr-only "Actions" head, which would otherwise anchor to the fixed dialog. */}
      <div className="relative max-h-[55vh] overflow-y-auto rounded-md border p-4">
        {/* Show prompt when fewer than 2 characters */}
        {searchValue.length < 2 && (
          <div className="flex min-h-72 flex-col items-center justify-center gap-2 text-gray-600">
            <p className="text-lg font-semibold">Search for a customer or property</p>
            <p className="text-center text-sm">
              Enter at least 2 characters to search by name, phone, email, or property address
            </p>
          </div>
        )}

        {/* Results list */}
        {searchValue.length >= 2 && (
          <div className="space-y-4">
            {/* Customers Section - Accordion */}
            <div className="rounded-md border border-gray-200">
              <button
                onClick={() => toggleSection('customers')}
                className="flex w-full items-center justify-between p-3 text-left hover:bg-gray-50"
              >
                <div className="flex items-center gap-2">
                  <span className="text-lg font-semibold text-gray-900">
                    Customers ({customers?.length ?? 0})
                  </span>
                  {isSearching && <LoadingIcon className="size-4" />}
                </div>
                {expandedSections.customers ? (
                  <ChevronDownIcon className="size-5 text-gray-500" />
                ) : (
                  <ChevronRightIcon className="size-5 text-gray-500" />
                )}
              </button>

              {expandedSections.customers && (
                <div className="overflow-x-auto border-t border-gray-200 p-3">
                  {/* Show loading state for customers */}
                  {isSearching && (
                    <div className="flex items-center justify-center py-4">
                      <LoadingIcon className="size-5" />
                      <span className="ml-2 text-sm text-gray-600">Searching customers...</span>
                    </div>
                  )}

                  {/* Show customer results when available */}
                  {!isSearching && customers && customers.length > 0 && (
                    <Table className="min-w-[920px] table-fixed [&>tbody>tr>td]:px-3 [&>tbody>tr>td]:py-2 [&>thead>tr>th]:h-8 [&>thead>tr>th]:px-3 [&>thead>tr>th]:text-xs [&>thead>tr>th]:font-semibold [&>thead>tr>th]:uppercase [&>thead>tr>th]:tracking-wide [&>thead>tr>th]:text-gray-500">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[20%]">Name</TableHead>
                          <TableHead className="w-[15%]">Business</TableHead>
                          <TableHead className="w-[104px] text-right">Properties</TableHead>
                          <TableHead className="w-[13%]">Phone</TableHead>
                          <TableHead className="w-[20%]">Email</TableHead>
                          <TableHead className="w-[184px]">
                            <span className="sr-only">Actions</span>
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {customers.map((customer) => {
                          const fullName = `${customer.firstname ?? ''} ${
                            customer.lastname ?? ''
                          }`.trim();
                          const businessname = customer.businessname?.trim();

                          return (
                            <Fragment key={customer.id}>
                              <TableRow>
                                <TableCell className="font-semibold text-gray-900">
                                  <div className="flex items-center gap-1.5">
                                    <span className="truncate" title={fullName || undefined}>
                                      {fullName}
                                    </span>
                                    {customer.doNotServe && (
                                      <span className="shrink-0 rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">
                                        Do Not Serve
                                      </span>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell className="text-gray-600">
                                  <span className="block truncate" title={businessname}>
                                    {businessname}
                                  </span>
                                </TableCell>
                                <TableCell className="text-right font-medium tabular-nums text-blue-600">
                                  {customer.countProperties ?? 0}
                                </TableCell>
                                <TableCell className="text-gray-600">
                                  <span className="block truncate" title={customer.phone}>
                                    {customer.phone}
                                  </span>
                                </TableCell>
                                <TableCell className="text-gray-600">
                                  <span className="block truncate" title={customer.email}>
                                    {customer.email}
                                  </span>
                                </TableCell>
                                <TableCell className="whitespace-nowrap text-right">
                                  <div className="flex justify-end gap-2">
                                    <Button
                                      variant="outline"
                                      onClick={() =>
                                        setExpandedCustomerJobs(
                                          expandedCustomerJobs === customer.id ? null : customer.id,
                                        )
                                      }
                                      size="sm"
                                    >
                                      View Jobs
                                    </Button>
                                    <Button onClick={() => onSelectCustomer(customer)} size="sm">
                                      Select
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>

                              {/* Jobs Accordion */}
                              {expandedCustomerJobs === customer.id && (
                                <TableRow className="hover:bg-transparent">
                                  <TableCell colSpan={6} className="p-3">
                                    <div className="max-w-full overflow-hidden py-1">
                                      <CustomerJobsAccordion customer={customer} />
                                    </div>
                                  </TableCell>
                                </TableRow>
                              )}
                            </Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}

                  {/* Show no customers message when search is complete and no results */}
                  {!isSearching && customers && customers.length === 0 && (
                    <div className="py-4 text-center text-gray-500">
                      <p className="text-sm">No customers found</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Properties Section - Accordion */}
            <div className="rounded-md border border-gray-200">
              <button
                onClick={() => toggleSection('properties')}
                className="flex w-full items-center justify-between p-3 text-left hover:bg-gray-50"
              >
                <div className="flex items-center gap-2">
                  <span className="text-lg font-semibold text-gray-900">
                    Properties ({userProperties?.length ?? 0})
                  </span>
                  {isPropertySearching && <LoadingIcon className="size-4" />}
                </div>
                {expandedSections.properties ? (
                  <ChevronDownIcon className="size-5 text-gray-500" />
                ) : (
                  <ChevronRightIcon className="size-5 text-gray-500" />
                )}
              </button>

              {expandedSections.properties && (
                <div className="overflow-x-auto border-t border-gray-200 p-3">
                  {/* Show loading state for properties */}
                  {isPropertySearching && (
                    <div className="flex items-center justify-center py-4">
                      <LoadingIcon className="size-5" />
                      <span className="ml-2 text-sm text-gray-600">Searching properties...</span>
                    </div>
                  )}

                  {/* Show property results when available */}
                  {!isPropertySearching && userProperties && userProperties.length > 0 && (
                    <Table className="min-w-[900px] table-fixed [&>tbody>tr>td]:px-3 [&>tbody>tr>td]:py-2 [&>thead>tr>th]:h-8 [&>thead>tr>th]:px-3 [&>thead>tr>th]:text-xs [&>thead>tr>th]:font-semibold [&>thead>tr>th]:uppercase [&>thead>tr>th]:tracking-wide [&>thead>tr>th]:text-gray-500">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[24%]">Address</TableHead>
                          <TableHead className="w-[17%]">City / State</TableHead>
                          <TableHead className="w-[22%]">Owner</TableHead>
                          <TableHead className="w-[16%]">Notes</TableHead>
                          <TableHead className="w-[184px]">
                            <span className="sr-only">Actions</span>
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {userProperties
                          .filter((userProperty) => userProperty.property)
                          .map((userProperty) => {
                            const property = userProperty.property!;
                            const user = userProperty.user;
                            const customerName =
                              user?.businessname ||
                              (user?.firstname && user?.lastname
                                ? `${user.firstname} ${user.lastname}`
                                : 'Unknown Customer');
                            const location = [property.city, property.state, property.zip]
                              .filter(Boolean)
                              .join(', ');
                            const ownerContact = [user?.phone, user?.email]
                              .filter(Boolean)
                              .join(' · ');

                            return (
                              <Fragment key={property.id}>
                                <TableRow>
                                  <TableCell className="font-semibold text-gray-900">
                                    <span className="block truncate" title={property.address}>
                                      {property.address}
                                    </span>
                                  </TableCell>
                                  <TableCell className="text-gray-600">
                                    <span className="block truncate" title={location || undefined}>
                                      {location}
                                    </span>
                                  </TableCell>
                                  <TableCell>
                                    <span
                                      className="block truncate font-medium text-blue-600"
                                      title={customerName}
                                    >
                                      {customerName}
                                    </span>
                                    {ownerContact && (
                                      <span
                                        className="block truncate text-xs text-gray-500"
                                        title={ownerContact}
                                      >
                                        {ownerContact}
                                      </span>
                                    )}
                                  </TableCell>
                                  <TableCell className="text-gray-500">
                                    <span className="block truncate" title={property.notes}>
                                      {property.notes}
                                    </span>
                                  </TableCell>
                                  <TableCell className="whitespace-nowrap text-right">
                                    <div className="flex justify-end gap-2">
                                      <Button
                                        variant="outline"
                                        onClick={() =>
                                          setExpandedPropertyJobs(
                                            expandedPropertyJobs === property.id
                                              ? null
                                              : property.id,
                                          )
                                        }
                                        size="sm"
                                      >
                                        View Jobs
                                      </Button>
                                      <Button
                                        onClick={() => onSelectProperty(property, user)}
                                        size="sm"
                                      >
                                        Select
                                      </Button>
                                    </div>
                                  </TableCell>
                                </TableRow>

                                {/* Jobs Accordion */}
                                {expandedPropertyJobs === property.id && (
                                  <TableRow className="hover:bg-transparent">
                                    <TableCell colSpan={5} className="p-3">
                                      <div className="max-w-full overflow-hidden py-1">
                                        <CustomerJobsAccordion
                                          customer={User.create({ id: property.userId })}
                                          propertyId={property.id}
                                        />
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </Fragment>
                            );
                          })}
                      </TableBody>
                    </Table>
                  )}

                  {/* Show no properties message when search is complete and no results */}
                  {!isPropertySearching && userProperties && userProperties.length === 0 && (
                    <div className="py-4 text-center text-gray-500">
                      <p className="text-sm">No properties found</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Overall no results message - only show when both searches are complete and no results */}
            {!isSearching &&
              !isPropertySearching &&
              customers &&
              customers.length === 0 &&
              userProperties &&
              userProperties.length === 0 && (
                <div className="py-8 text-center text-gray-500">
                  <p>No customers or properties found matching &quot;{searchValue}&quot;</p>
                  <p className="text-sm">Try a different search term</p>
                </div>
              )}
          </div>
        )}
      </div>
    </div>
  );

  const renderNewCustomerTab = () => (
    <FormContext {...newCustomerForm}>
      <form onSubmit={newCustomerForm.handleSubmit(handleCreateCustomer)} className="space-y-4">
        <fieldset disabled={createCustomerMutation.isPending} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={newCustomerForm.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>First Name *</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="First name" className="h-12" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={newCustomerForm.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Last Name *</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Last name" className="h-12" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={newCustomerForm.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Phone Number *</FormLabel>
                <FormControl>
                  <PhoneInput
                    {...field}
                    countries={allowedCountries}
                    defaultCountry="US"
                    placeholder="Phone number"
                    className="h-12"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* USER_CONTACTS DISABLED (temporary):
          {additionalPhonesArray.fields.map((row, idx) => (
            <div key={row.id} className="flex items-start gap-2">
              <FormField
                control={newCustomerForm.control}
                name={`additionalPhones.${idx}.typeId`}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger className="h-12 w-28 shrink-0">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {USER_CONTACT_KINDS.phone.types.map((t) => (
                            <SelectItem key={t.id} value={String(t.id)}>
                              {t.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={newCustomerForm.control}
                name={`additionalPhones.${idx}.value`}
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <PhoneInput
                        {...field}
                        countries={allowedCountries}
                        defaultCountry="US"
                        placeholder="Phone number"
                        className="h-12"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-12 shrink-0"
                aria-label="Remove phone"
                onClick={() => additionalPhonesArray.remove(idx)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <button
            type="button"
            className="inline-flex items-center gap-1 text-sm font-medium text-red-800 hover:text-red-900"
            onClick={() =>
              additionalPhonesArray.append({
                value: '',
                typeId: String(USER_CONTACT_KINDS.phone.defaultTypeId),
              })
            }
          >
            <Plus className="h-4 w-4" />
            Add another phone
          </button>
          */}

          <FormField
            control={newCustomerForm.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email Address</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Email address" className="h-12" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* USER_CONTACTS DISABLED (temporary):
          {additionalEmailsArray.fields.map((row, idx) => (
            <div key={row.id} className="flex items-start gap-2">
              <FormField
                control={newCustomerForm.control}
                name={`additionalEmails.${idx}.typeId`}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger className="h-12 w-28 shrink-0">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {USER_CONTACT_KINDS.email.types.map((t) => (
                            <SelectItem key={t.id} value={String(t.id)}>
                              {t.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={newCustomerForm.control}
                name={`additionalEmails.${idx}.value`}
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <Input {...field} type="email" placeholder="Email address" className="h-12" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-12 shrink-0"
                aria-label="Remove email"
                onClick={() => additionalEmailsArray.remove(idx)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <button
            type="button"
            className="inline-flex items-center gap-1 text-sm font-medium text-red-800 hover:text-red-900"
            onClick={() =>
              additionalEmailsArray.append({
                value: '',
                typeId: String(USER_CONTACT_KINDS.email.defaultTypeId),
              })
            }
          >
            <Plus className="h-4 w-4" />
            Add another email
          </button>
          */}

          <FormField
            control={newCustomerForm.control}
            name="company"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Company (Optional)</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Company" className="h-12" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={newCustomerForm.control}
            name="referralSourceId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Referral Source</FormLabel>
                <FormControl>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select referral source" />
                    </SelectTrigger>
                    <SelectContent>
                      {referralSources.map((s) => (
                        <SelectItem key={s.id} value={String(s.id)}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {isReferralOther && (
            <FormField
              control={newCustomerForm.control}
              name="referralSourceOther"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Please specify</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="How did they hear about us?" className="h-12" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <FormField
            control={newCustomerForm.control}
            name="contactPreferenceId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Contact Preference</FormLabel>
                <FormControl>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select contact preference" />
                    </SelectTrigger>
                    <SelectContent>
                      {contactPreferences.map((c) => (
                        <SelectItem key={c.id} value={String(c.id)}>
                          {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="border-t pt-4">
            <p className="mb-3 text-sm font-medium text-gray-700">Property Address</p>

            <FormField
              control={newCustomerForm.control}
              name="streetAddress"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Street Address *</FormLabel>
                  <FormControl>
                    <AddressSearchInput
                      value={field.value}
                      onChange={field.onChange}
                      onSelect={(a) => {
                        newCustomerForm.setValue('streetAddress', a.address, {
                          shouldValidate: true,
                        });
                        newCustomerForm.setValue('city', a.city, { shouldValidate: true });
                        newCustomerForm.setValue('state', a.state, { shouldValidate: true });
                        newCustomerForm.setValue('zipCode', a.zip, { shouldValidate: true });
                      }}
                      placeholder="Search address..."
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="mt-3 grid grid-cols-5 gap-4">
              <FormField
                control={newCustomerForm.control}
                name="city"
                render={({ field }) => (
                  <FormItem className="col-span-2">
                    <FormLabel>City *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="City" className="h-10" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={newCustomerForm.control}
                name="state"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>State *</FormLabel>
                    <FormControl>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger>
                          <SelectValue placeholder="State" />
                        </SelectTrigger>
                        <SelectContent>
                          {USA_STATES_OPTIONS.map((s) => {
                            const val = typeof s === 'string' ? s : (s as any).value;
                            const label = typeof s === 'string' ? s : (s as any).label;
                            return (
                              <SelectItem key={String(val)} value={String(val)}>
                                {String(label)}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={newCustomerForm.control}
                name="zipCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>ZIP Code *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="12345" className="h-10" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={newCustomerForm.control}
                name="storeNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Store/Unit Number (optional)</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="e.g. 1234" className="h-10" />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            <div className="mt-3">
              <FormField
                control={newCustomerForm.control}
                name="propertyOwnershipTypeId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Property Ownership *</FormLabel>
                    <FormControl>
                      <Tabs value={field.value} onValueChange={field.onChange} className="w-full">
                        <TabsList className="grid w-full grid-cols-2">
                          <TabsTrigger value="1">Owner-Occupied</TabsTrigger>
                          <TabsTrigger value="2">Tenant-Occupied</TabsTrigger>
                        </TabsList>
                      </Tabs>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="mt-3">
              <FormField
                control={newCustomerForm.control}
                name="propertyNotes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Property Notes</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        placeholder="Any special instructions or details..."
                        className="h-20"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </div>

          <FormField
            control={newCustomerForm.control}
            name="billingSameAsProperty"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center space-x-3 space-y-0 rounded-md border p-3">
                <FormControl>
                  <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                </FormControl>
                <div>
                  <FormLabel className="cursor-pointer text-sm font-medium">
                    Billing same as property
                  </FormLabel>
                  <p className="text-xs text-gray-500">
                    Uncheck if the billing address differs from the property address above
                  </p>
                </div>
              </FormItem>
            )}
          />

          {!newCustomerForm.watch('billingSameAsProperty') && (
            <div className="space-y-3 rounded-md border border-dashed p-4">
              <p className="text-sm font-medium text-gray-700">Billing Address</p>

              <FormField
                control={newCustomerForm.control}
                name="billingStreetAddress"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Street Address *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Billing street address" className="h-10" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-4 gap-4">
                <FormField
                  control={newCustomerForm.control}
                  name="billingCity"
                  render={({ field }) => (
                    <FormItem className="col-span-2">
                      <FormLabel>City *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="City" className="h-10" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={newCustomerForm.control}
                  name="billingState"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>State *</FormLabel>
                      <FormControl>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger>
                            <SelectValue placeholder="State" />
                          </SelectTrigger>
                          <SelectContent>
                            {USA_STATES_OPTIONS.map((s) => {
                              const val = typeof s === 'string' ? s : (s as any).value;
                              const label = typeof s === 'string' ? s : (s as any).label;
                              return (
                                <SelectItem key={String(val)} value={String(val)}>
                                  {String(label)}
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={newCustomerForm.control}
                  name="billingZipCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>ZIP Code *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="12345" className="h-10" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          )}

          <div className="flex justify-end pt-2">
            <Button
              type="submit"
              disabled={!newCustomerForm.formState.isValid || createCustomerMutation.isPending}
              isLoading={createCustomerMutation.isPending}
            >
              Create Customer & Continue
            </Button>
          </div>
        </fieldset>
      </form>
    </FormContext>
  );

  // -------------------- JSX --------------------
  return (
    <div className="flex w-full flex-col gap-6">
      <Tabs value={tab} onValueChange={(val) => setTab(val as typeof tab)} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="search">Search Customer/Property</TabsTrigger>
          <TabsTrigger value="new">New Customer</TabsTrigger>
        </TabsList>

        <TabsContent value="search" className="mt-4">
          {renderSearchTab()}
        </TabsContent>
        <TabsContent value="new" className="mt-4">
          {renderNewCustomerTab()}
        </TabsContent>
      </Tabs>
    </div>
  );
};
